import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { usePresence, useOtherPeers } from "../lib/presence";
import { PresenceAvatar } from "../components/PresenceAvatars";
import type {
  User,
  RepoState,
  LogLine,
  LessonStep,
  Lesson as LessonT,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
  Commit,
} from "../lib/types";
import {
  LESSON_COMMITS,
  INITIAL_REPO,
  INITIAL_LOG,
  QUIZ_QUESTIONS,
  GLOSSARY,
  CHEATSHEET,
} from "../data/lessons/commits-branches";
import {
  LESSON_REMOTE,
  INITIAL_REPO_REMOTE,
  INITIAL_LOG_REMOTE,
  QUIZ_QUESTIONS_REMOTE,
  GLOSSARY_REMOTE,
  CHEATSHEET_REMOTE,
  TEAMMATE_COMMIT,
  TEAMMATE_README,
} from "../data/lessons/remote";
import {
  LESSON_PULL_REQUESTS,
  INITIAL_REPO_PULL_REQUESTS,
  INITIAL_LOG_PULL_REQUESTS,
  QUIZ_QUESTIONS_PULL_REQUESTS,
  GLOSSARY_PULL_REQUESTS,
  CHEATSHEET_PULL_REQUESTS,
  ORIGINAL_APP_JSX,
} from "../data/lessons/pull-requests";
import {
  LESSON_MERGE_CONFLICTS,
  INITIAL_REPO_MERGE_CONFLICTS,
  INITIAL_LOG_MERGE_CONFLICTS,
  QUIZ_QUESTIONS_MERGE_CONFLICTS,
  GLOSSARY_MERGE_CONFLICTS,
  CHEATSHEET_MERGE_CONFLICTS,
  CONFLICT_SCENARIO,
} from "../data/lessons/merge-conflicts";
import {
  LESSON_UNDOING,
  INITIAL_REPO_UNDOING,
  INITIAL_LOG_UNDOING,
  QUIZ_QUESTIONS_UNDOING,
  GLOSSARY_UNDOING,
  CHEATSHEET_UNDOING,
  GOOD_TIP_COMMIT,
  OOPS_COMMIT,
  REFLOG_RESET_TARGET,
} from "../data/lessons/undoing";
import { SafetyLadder } from "../components/SafetyLadder";
import { Reflog } from "../components/Reflog";
import { ConflictResolver } from "../components/ConflictResolver";
import { ConflictDiagram } from "../components/ConflictDiagram";
import {
  PRPanel,
  INITIAL_PR_STATE,
  TEAMMATE_COMMENT,
  YOUR_REPLY,
  JAMIE_APPROVAL,
  type PRState,
} from "../components/PRPanel";
import { PRConceptDiagram } from "../components/PRConceptDiagram";
import {
  deepClone,
  editFile,
  stageAll,
  makeCommit,
  createAndSwitchToFeature,
  switchBranch,
  mergeInto,
  cloneFromOrigin,
  pushBranch,
  fetchOrigin,
  pullOrigin,
  aheadBehind,
  startConflictMerge,
  resolveHunk as resolveHunkOp,
  finalizeConflict,
  restoreFile,
  resetBranch,
  revertCommit,
  applyReflog,
} from "../lib/git-sim";
import type { HunkResolution } from "../lib/types";
import { computeDiff, truncateMsg } from "../lib/diff";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { GitGraph } from "../components/GitGraph";
import { RemoteAwareGraph } from "../components/RemoteAwareGraph";
import { CodeEditor } from "../components/CodeEditor";
import {
  TerminalLog,
  ActionRow,
  Action,
} from "../components/Terminal";
import { Quiz } from "../components/Quiz";
import { Chatbot } from "../components/Chatbot";
import { COURSES } from "../data/courses";
import { I } from "../components/Icons";
import {
  setStepDone,
  setQuizScore,
  setCursor,
  readCursor,
  setCourseComplete,
  useProgress,
} from "../lib/progress";
import { isCourseComplete } from "../lib/course-progress";

interface LessonBundle {
  lesson: LessonT;
  initialRepo: RepoState;
  initialLog: LogLine[];
  quizQuestions: QuizQuestion[];
  glossary: GlossaryItem[];
  cheatsheet: CheatsheetItem[];
  nextCourseId?: string;
  nextCourseTitle?: string;
  nextCourseBlurb?: string;
  /** Optional hook: when entering a step, optionally mutate the repo (e.g. inject teammate commit). */
  onEnterStep?: (stepId: string, repo: RepoState) => RepoState | null;
  /** Course-meta subtitle line in the left rail. */
  courseMetaTop: string;
  courseSubtitle: string;
}

const LESSON_BUNDLES: Record<string, LessonBundle> = {
  "commits-branches": {
    lesson: LESSON_COMMITS,
    initialRepo: INITIAL_REPO,
    initialLog: INITIAL_LOG,
    quizQuestions: QUIZ_QUESTIONS,
    glossary: GLOSSARY,
    cheatsheet: CHEATSHEET,
    nextCourseId: "remote",
    nextCourseTitle: "Remote vs local — push, pull, and origin",
    nextCourseBlurb:
      "Now that you can commit and branch locally, the next course connects your laptop to GitHub. You'll learn what origin really means.",
    courseMetaTop: "Course 02 · Lesson 2 of 7",
    courseSubtitle: "Commits, branches, fast-forward merges",
  },
  "merge-conflicts": {
    lesson: LESSON_MERGE_CONFLICTS,
    initialRepo: INITIAL_REPO_MERGE_CONFLICTS,
    initialLog: INITIAL_LOG_MERGE_CONFLICTS,
    quizQuestions: QUIZ_QUESTIONS_MERGE_CONFLICTS,
    glossary: GLOSSARY_MERGE_CONFLICTS,
    cheatsheet: CHEATSHEET_MERGE_CONFLICTS,
    nextCourseId: "undoing",
    nextCourseTitle: "Undoing & recovering",
    nextCourseBlurb:
      "You've handled the scariest-looking moment in git. The last course covers the rest of the safety net — revert, reset, reflog. The 'I broke something, get me back' toolkit.",
    courseMetaTop: "Course 05 · Lesson 5 of 6",
    courseSubtitle: "Trigger, read, resolve, commit",
  },
  "pull-requests": {
    lesson: LESSON_PULL_REQUESTS,
    initialRepo: INITIAL_REPO_PULL_REQUESTS,
    initialLog: INITIAL_LOG_PULL_REQUESTS,
    quizQuestions: QUIZ_QUESTIONS_PULL_REQUESTS,
    glossary: GLOSSARY_PULL_REQUESTS,
    cheatsheet: CHEATSHEET_PULL_REQUESTS,
    nextCourseId: "merge-conflicts",
    nextCourseTitle: "Merge conflicts, calmly",
    nextCourseBlurb:
      "You can branch, push, and merge through a PR. The next course covers what happens when two people edit the same line — and how to resolve it without panic.",
    courseMetaTop: "Course 04 · Lesson 4 of 6",
    courseSubtitle: "Open, review, merge",
  },
  undoing: {
    lesson: LESSON_UNDOING,
    initialRepo: INITIAL_REPO_UNDOING,
    initialLog: INITIAL_LOG_UNDOING,
    quizQuestions: QUIZ_QUESTIONS_UNDOING,
    glossary: GLOSSARY_UNDOING,
    cheatsheet: CHEATSHEET_UNDOING,
    courseMetaTop: "Course 06 · Lesson 6 of 6",
    courseSubtitle: "Restore, reset, revert, reflog",
    onEnterStep: (stepId, repo) => {
      // Reset between steps so each sandbox starts from a known state.
      if (stepId === "restore") {
        // Pre-mess App.jsx so the learner has something to restore.
        const f = repo.files["App.jsx"];
        if (!f) return null;
        if (f.status !== "clean") return null;
        const messed = f.committed.replace(
          "operating system",
          "operating sytsem",
        );
        return {
          ...repo,
          files: {
            ...repo.files,
            "App.jsx": { ...f, content: messed, status: "modified" },
          },
        };
      }
      if (stepId === "reflog") {
        // Pre-run a hard reset to "lose" the bad commit, so the reflog has a
        // recoverable entry. Idempotent: skip if already reset.
        if (repo.branches.main === REFLOG_RESET_TARGET) return null;
        const res = resetBranch(repo, "hard", REFLOG_RESET_TARGET);
        return res.repo;
      }
      return null;
    },
  },
  remote: {
    lesson: LESSON_REMOTE,
    initialRepo: INITIAL_REPO_REMOTE,
    initialLog: INITIAL_LOG_REMOTE,
    quizQuestions: QUIZ_QUESTIONS_REMOTE,
    glossary: GLOSSARY_REMOTE,
    cheatsheet: CHEATSHEET_REMOTE,
    nextCourseId: "pull-requests",
    nextCourseTitle: "Pull requests on GitHub",
    nextCourseBlurb:
      "Now that you can sync with GitHub, the next course is about opening a pull request — how teams actually merge work into main on a real project.",
    courseMetaTop: "Course 03 · Lesson 3 of 5",
    courseSubtitle: "Clone, push, fetch, pull",
    onEnterStep: (stepId, repo) => {
      if (stepId !== "pull") return null;
      const origin = repo.remotes?.origin;
      if (!origin) return null;
      // Idempotent: don't double-inject.
      if (origin.commits.find((c) => c.id === TEAMMATE_COMMIT.id)) return null;
      const tipId = origin.branches.main;
      const tipCommit = origin.commits.find((c) => c.id === tipId);
      const newCommit: Commit = {
        id: TEAMMATE_COMMIT.id,
        msg: TEAMMATE_COMMIT.msg,
        branch: TEAMMATE_COMMIT.branch,
        parent: tipId,
        x: (tipCommit?.x ?? 0) + 1,
      };
      return {
        ...repo,
        remotes: {
          ...repo.remotes,
          origin: {
            ...origin,
            commits: [...origin.commits, newCommit],
            branches: { ...origin.branches, main: TEAMMATE_COMMIT.id },
            files: { ...origin.files, "README.md": TEAMMATE_README },
          },
        },
      };
    },
  },
};

interface Props {
  user: User;
  courseId: string;
  onSignOut: () => void;
}

export function Lesson({ user, courseId, onSignOut }: Props) {
  const [, setLocation] = useLocation();
  const onBackToDashboard = () => setLocation("/dashboard");
  const bundle = LESSON_BUNDLES[courseId] ?? LESSON_BUNDLES["commits-branches"]!;
  const {
    lesson,
    initialRepo,
    initialLog,
    quizQuestions,
    glossary,
    cheatsheet,
    nextCourseId,
    nextCourseTitle,
    nextCourseBlurb,
    onEnterStep,
    courseMetaTop,
    courseSubtitle,
  } = bundle;
  const isRemoteLesson = !!initialRepo.remotes;

  // Whether the next course an admin has locked — so the "Start next course"
  // CTAs can disable themselves and say so instead of dead-ending the learner.
  const [lockedCourseIds, setLockedCourseIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    fetch("/api/courses/locks", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { locked: [] }))
      .then((data: { locked?: string[] }) => {
        if (alive) setLockedCourseIds(new Set(data.locked ?? []));
      })
      .catch(() => {
        // Fail open — leave the next-course CTA enabled.
      });
    return () => {
      alive = false;
    };
  }, []);
  const nextCourseLocked = nextCourseId
    ? lockedCourseIds.has(nextCourseId)
    : false;

  // Explicit completion drives the final-step CTA: until the learner marks this
  // course complete, the button reads "Mark course as complete" and the next
  // course stays gated.
  const progress = useProgress();
  const courseComplete = isCourseComplete(progress, courseId);

  // Resume on the step the learner left off on (clamped to a valid index).
  const [stepIdx, setStepIdx] = useState(() =>
    Math.min(Math.max(readCursor(courseId), 0), lesson.steps.length - 1),
  );
  const step = lesson.steps[stepIdx];

  const [repo, setRepo] = useState<RepoState>(() => deepClone(initialRepo));
  const initialActiveFile = Object.keys(initialRepo.files)[0] ?? "App.jsx";
  const [activeFile, setActiveFile] = useState(initialActiveFile);
  const [log, setLog] = useState<LogLine[]>(initialLog);
  const [animLastCommit, setAnimLastCommit] = useState(false);
  const [syncFlash, setSyncFlash] = useState<"push" | "pull" | "fetch" | null>(
    null,
  );
  const [goalDone, setGoalDone] = useState<Record<number, boolean>>({});
  const [prState, setPRState] = useState<PRState>(() => ({ ...INITIAL_PR_STATE }));
  const [commitMsgOpen, setCommitMsgOpen] = useState(false);
  const [commitMsgDraft, setCommitMsgDraft] = useState("");

  function flashSync(kind: "push" | "pull" | "fetch") {
    setSyncFlash(kind);
    setTimeout(() => setSyncFlash(null), 700);
  }

  const filesArr = Object.entries(repo.files);
  const modifiedFiles = filesArr
    .filter(([, f]) => f.status === "modified")
    .map(([n]) => n);
  const stagedFiles = filesArr
    .filter(([, f]) => f.status === "staged")
    .map(([n]) => n);

  function appendLog(...lines: LogLine[]) {
    setLog((l) => [...l, ...lines]);
  }

  function onContentChange(name: string, content: string) {
    setRepo((r) => editFile(r, name, content));
  }

  function onStage() {
    const res = stageAll(repo);
    if (res.log.length) {
      setRepo(res.repo);
      appendLog(...res.log);
      if (step.goal === "stage") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onCommit() {
    // Merge commits keep their auto-generated "Merge branch X into Y" message —
    // git itself does the same. For all other commits, prompt for a message so
    // learners experience the full `git commit -m "..."` flow.
    if (!repo.pendingMerge && stagedFiles.length > 0) {
      setCommitMsgDraft("");
      setCommitMsgOpen(true);
      return;
    }
    runCommit();
  }

  function runCommit(msgOverride?: string) {
    const wasMerge = !!repo.pendingMerge;
    const res = makeCommit(repo, msgOverride);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.committed) {
      setAnimLastCommit(true);
      setTimeout(() => setAnimLastCommit(false), 500);
      const branch = repo.head;
      if (step.goal === "commit") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
      if (step.goal === "branch-and-commit" && branch === "feature") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
      if (step.goal === "resolve-conflict" && wasMerge) {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onCreateBranch() {
    const res = createAndSwitchToFeature(repo);
    setRepo(res.repo);
    appendLog(...res.log);
  }

  function onSwitch(name: string) {
    const res = switchBranch(repo, name);
    setRepo(res.repo);
    appendLog(...res.log);
  }

  function onMerge() {
    const res = mergeInto(repo, "main", "feature");
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.merged && step.goal === "merge") {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
  }

  function onAttemptConflictMerge() {
    if (repo.conflict || repo.pendingMerge) return;
    const res = startConflictMerge(repo, "feature", CONFLICT_SCENARIO);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.conflicted) {
      setActiveFile(CONFLICT_SCENARIO.file);
      if (step.goal === "trigger-conflict") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onResolveHunk(hunkIndex: number, choice: HunkResolution, customText?: string) {
    setRepo((r) => resolveHunkOp(r, hunkIndex, choice, customText));
  }

  function onMarkResolved() {
    const res = finalizeConflict(repo);
    setRepo(res.repo);
    appendLog(...res.log);
  }

  // ----------------- Undo / recovery handlers (course 06) -----------------
  function onRestore(filename: string) {
    const res = restoreFile(repo, filename);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.restored && step.goal === "restore-file") {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
  }

  function onReset(mode: "soft" | "mixed" | "hard", targetCommitId: string) {
    const res = resetBranch(repo, mode, targetCommitId);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.reset && step.goal === "reset-commit") {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
  }

  function onRevert(targetCommitId: string) {
    const res = revertCommit(repo, targetCommitId);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.reverted) {
      setAnimLastCommit(true);
      setTimeout(() => setAnimLastCommit(false), 500);
      if (step.goal === "revert-commit") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onReflogRecover(reflogIndex: number) {
    const res = applyReflog(repo, reflogIndex);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.recovered && step.goal === "reflog-recover") {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
  }

  function onClone() {
    const res = cloneFromOrigin(repo);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.cloned) {
      flashSync("pull");
      if (step.goal === "clone") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
      const firstFile = Object.keys(res.repo.files)[0];
      if (firstFile) setActiveFile(firstFile);
    }
  }

  function onPush() {
    const res = pushBranch(repo, repo.head);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.pushed) {
      flashSync("push");
      if (step.goal === "push") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onFetch() {
    const res = fetchOrigin(repo, repo.head);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.fetched) {
      flashSync("fetch");
      if (step.goal === "fetch") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function onPull() {
    const res = pullOrigin(repo, repo.head);
    setRepo(res.repo);
    appendLog(...res.log);
    if (res.pulled) {
      flashSync("pull");
      if (step.goal === "pull") {
        setGoalDone((g) => ({ ...g, [stepIdx]: true }));
      }
    }
  }

  function resetRepo() {
    // Full start-over: the sandbox repo accumulates across steps, so a reset
    // that left the learner on a later step would strand them with step-1 state
    // under step-N instructions. Send them back to step 1 with pristine state.
    setRepo(deepClone(initialRepo));
    setLog(initialLog);
    setActiveFile(initialActiveFile);
    setGoalDone({});
    setPRState({ ...INITIAL_PR_STATE });
    setCommitMsgOpen(false);
    setCommitMsgDraft("");
    setStepIdx(0);
    setCursor(lesson.courseId, 0);
  }

  // ----------------- PR action handlers (pull-requests course) -----------------
  function onPRCreate() {
    if (prState.exists) return;
    setPRState((s) => ({ ...s, exists: true }));
    appendLog(
      { kind: "cmd", text: "gh pr create --title 'Refresh homepage copy' --base main --head feature" },
      { kind: "ok", text: `https://github.com/nymbl/atlas/pull/${prState.number}` },
      { kind: "out", text: "Created pull request #42 (feature → main)" },
    );
    setGoalDone((g) => ({ ...g, [stepIdx]: true }));
  }

  function onPRComment() {
    if (prState.comments.length >= 2) return;
    if (prState.comments.length === 0) {
      setPRState((s) => ({ ...s, comments: [...s.comments, TEAMMATE_COMMENT] }));
      appendLog(
        { kind: "info", text: "jamie-tan left a comment on pull request #42" },
      );
    } else {
      setPRState((s) => ({ ...s, comments: [...s.comments, YOUR_REPLY] }));
      appendLog({ kind: "ok", text: "Reply posted to pull request #42" });
    }
  }

  function onPRApprove() {
    if (prState.reviews.some((r) => r.state === "approved")) return;
    setPRState((s) => ({ ...s, reviews: [...s.reviews, JAMIE_APPROVAL] }));
    appendLog(
      { kind: "cmd", text: "gh pr review 42 --approve" },
      { kind: "ok", text: "jamie-tan approved pull request #42" },
    );
    setGoalDone((g) => ({ ...g, [stepIdx]: true }));
  }

  function onPRMerge() {
    if (prState.merged) return;
    const approved = prState.reviews.some((r) => r.state === "approved");
    if (!approved) return;

    // Merge happens on origin: collapse feature commits into a single squash
    // commit on origin/main. Local main becomes behind until the learner pulls.
    setRepo((r) => {
      if (!r.remotes?.origin) return r;
      const origin = r.remotes.origin;
      const featureCommits = r.commits.filter((c) => c.branch === "feature");
      if (featureCommits.length === 0) return r;
      const originTipId = origin.branches.main;
      const originTip = origin.commits.find((c) => c.id === originTipId);
      const squashId = "9f8e7d6";
      const squashCommit = {
        id: squashId,
        msg: `Refresh homepage copy (#${prState.number})`,
        branch: "main",
        parent: originTipId,
        x: (originTip?.x ?? 0) + 1,
      };
      // Use the learner's current App.jsx as the merged content on origin.
      const mergedAppJsx = r.files["App.jsx"]?.committed ?? origin.files["App.jsx"];
      return {
        ...r,
        remotes: {
          ...r.remotes,
          origin: {
            ...origin,
            commits: [...origin.commits, squashCommit],
            branches: { ...origin.branches, main: squashId },
            files: { ...origin.files, "App.jsx": mergedAppJsx },
          },
        },
      };
    });
    setPRState((s) => ({ ...s, merged: true }));
    setSyncFlash("push");
    setTimeout(() => setSyncFlash(null), 700);
    appendLog(
      { kind: "cmd", text: "gh pr merge 42 --squash --delete-branch" },
      { kind: "ok", text: "Pull request #42 merged into main as squash commit 9f8e7d6" },
      { kind: "out", text: "Deleted branch feature on origin." },
    );
    setGoalDone((g) => ({ ...g, [stepIdx]: true }));
  }

  // Intro/wrap steps auto-mark themselves done when viewed.
  useEffect(() => {
    if (
      step.kind === "intro" ||
      step.kind === "github" ||
      step.kind === "wrap" ||
      step.kind === "conflict-anatomy"
    ) {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
    setStepDone(lesson.courseId, step.id);
    setCursor(lesson.courseId, stepIdx);
  }, [stepIdx, step.id, step.kind, lesson.courseId]);

  // Re-mark PR steps as done if their action already ran (e.g. after navigating back).
  useEffect(() => {
    if (step.kind !== "pr") return;
    if (step.id === "open-pr" && prState.exists) {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
    if (step.id === "review" && prState.reviews.some((r) => r.state === "approved")) {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
    if (step.id === "merge-pr" && prState.merged) {
      setGoalDone((g) => ({ ...g, [stepIdx]: true }));
    }
  }, [stepIdx, step.id, step.kind, prState]);

  // Per-lesson "on enter step" hook (e.g. inject teammate commit on origin for the pull step).
  useEffect(() => {
    if (!onEnterStep) return;
    const next = onEnterStep(step.id, repo);
    if (next) setRepo(next);
    // Intentionally only depends on stepIdx — we want this to fire on step entry, not repo changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  const presence = usePresence();
  useEffect(() => {
    presence.setLocation({
      screen: "lesson",
      lessonId: lesson.courseId,
      step: stepIdx,
    });
  }, [presence, stepIdx, lesson.courseId]);

  const stepsRef = useRef<HTMLDivElement | null>(null);
  const stepRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [stepOffsets, setStepOffsets] = useState<number[]>([]);

  useLayoutEffect(() => {
    function measure() {
      const offsets = stepRefs.current.map((el) =>
        el ? el.offsetTop + el.offsetHeight / 2 : 0,
      );
      setStepOffsets(offsets);
    }
    measure();
    const ro = new ResizeObserver(measure);
    if (stepsRef.current) ro.observe(stepsRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [lesson.steps.length]);

  const otherPeers = useOtherPeers();
  const peersInLesson = otherPeers.filter(
    (p) =>
      p.location.screen === "lesson" &&
      p.location.lessonId === lesson.courseId &&
      typeof p.location.step === "number",
  );
  const peersByStep = new Map<number, typeof peersInLesson>();
  for (const p of peersInLesson) {
    const s = p.location.step!;
    const arr = peersByStep.get(s) ?? [];
    arr.push(p);
    peersByStep.set(s, arr);
  }

  function goNext() {
    if (stepIdx < lesson.steps.length - 1) setStepIdx(stepIdx + 1);
  }
  function goPrev() {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  const progressPct = Math.round(
    ((stepIdx + 1) / lesson.steps.length) * 100,
  );

  return (
    <div className="gf-shell">
      <Sidebar active="courses" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "My courses", href: "/dashboard" },
            { t: lesson.courseTitle, strong: true },
          ]}
          right={
            <>
              <button
                className="gf-btn gf-btn--ghost"
                onClick={onBackToDashboard}
              >
                <I.ChevronLeft size={14} /> All courses
              </button>
              <button
                className="gf-btn gf-btn--secondary"
                onClick={resetRepo}
                title="Start the course over from step 1"
              >
                <I.Refresh size={14} /> Reset
              </button>
            </>
          }
        />

        <div className="gf-lesson">
          <div className="gf-lesson__sidebar">
            <div className="gf-lesson__course-meta">
              {courseMetaTop}
            </div>
            <div className="gf-lesson__course-title">{lesson.title}</div>
            <div className="gf-lesson__course-sub">
              {courseSubtitle}
            </div>

            <div className="gf-lesson__steps" ref={stepsRef}>
              {lesson.steps.map((s, i) => {
                const cls = ["gf-lesson__step"];
                if (i === stepIdx) cls.push("is-active");
                if (i < stepIdx) cls.push("is-done");
                return (
                  <button
                    key={s.id}
                    ref={(el) => {
                      stepRefs.current[i] = el;
                    }}
                    className={cls.join(" ")}
                    onClick={() => setStepIdx(i)}
                  >
                    <span className="gf-lesson__step-num">
                      {i < stepIdx ? <I.Check size={12} /> : i + 1}
                    </span>
                    <span className="gf-lesson__step-body">
                      <span className="gf-lesson__step-t">{s.shortTitle}</span>
                      <span className="gf-lesson__step-sub">{s.shortSub}</span>
                    </span>
                  </button>
                );
              })}
              {peersInLesson.map((p) => {
                const idx = p.location.step!;
                const top = stepOffsets[idx];
                if (top == null) return null;
                const sameStep = peersByStep.get(idx) ?? [];
                const order = sameStep.findIndex(
                  (q) => q.sessionId === p.sessionId,
                );
                return (
                  <div
                    key={p.sessionId}
                    className="gf-lesson__step-badges"
                    style={{
                      top: top - 12,
                      transform: `translate(${order * -10}px, 0)`,
                    }}
                  >
                    <PresenceAvatar peer={p} size={24} />
                  </div>
                );
              })}
            </div>

            <div className="gf-lesson__progress">
              <div className="gf-lesson__progress-row">
                <span>Lesson progress</span>
                <span className="gf-lesson__progress-val">
                  {progressPct}%
                </span>
              </div>
              <div className="gf-lesson__progress-bar">
                <div
                  className="gf-lesson__progress-bar-fill"
                  style={{ width: `${progressPct}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="gf-lesson__body">
            <div className="gf-step-head">
              <div className="gf-step-head__left">
                <div className="gf-step-head__eyebrow">{step.eyebrow}</div>
                <h1>{step.title}</h1>
                <p className="gf-step-head__sub">{step.sub}</p>
              </div>
              <div className="gf-step-head__nav">
                <button
                  className="gf-btn gf-btn--secondary"
                  onClick={goPrev}
                  disabled={stepIdx === 0}
                >
                  <I.ChevronLeft size={14} /> Back
                </button>
                {stepIdx === lesson.steps.length - 1 &&
                  (!courseComplete ? (
                    // Final step, not yet completed — a subtle action beside
                    // Next step. Marking complete unlocks the next course.
                    <button
                      className="gf-btn gf-btn--ghost"
                      onClick={() => setCourseComplete(courseId)}
                    >
                      <I.Check size={14} /> Mark course as complete
                    </button>
                  ) : nextCourseId ? (
                    // Completed — the next course is now unlocked by sequence; an
                    // admin lock can still hold it shut.
                    <button
                      className="gf-btn gf-btn--gradient"
                      onClick={() => setLocation(`/lesson/${nextCourseId}`)}
                      disabled={nextCourseLocked}
                      aria-disabled={nextCourseLocked}
                      title={
                        nextCourseLocked
                          ? "The next course is locked by your instructor."
                          : undefined
                      }
                    >
                      {nextCourseLocked ? (
                        <>
                          <I.Lock size={14} /> Next course is locked
                        </>
                      ) : (
                        <>
                          Start next course <I.ArrowRight size={14} />
                        </>
                      )}
                    </button>
                  ) : (
                    // Completed the final course in the track.
                    <button
                      className="gf-btn gf-btn--ghost"
                      onClick={onBackToDashboard}
                    >
                      <I.Check size={14} /> Course complete
                    </button>
                  ))}
                <button
                  className={`gf-btn ${goalDone[stepIdx] ? "gf-btn--gradient" : "gf-btn--primary"}`}
                  onClick={goNext}
                  disabled={stepIdx === lesson.steps.length - 1}
                >
                  Next step <I.ArrowRight size={14} />
                </button>
              </div>
            </div>

            <StepBody
              step={step}
              repo={repo}
              prState={prState}
              onPRCreate={onPRCreate}
              onPRComment={onPRComment}
              onPRApprove={onPRApprove}
              onPRMerge={onPRMerge}
              animLastCommit={animLastCommit}
              syncFlash={syncFlash}
              activeFile={activeFile}
              onActiveFileChange={setActiveFile}
              onContentChange={onContentChange}
              onStage={onStage}
              onCommit={onCommit}
              onCreateBranch={onCreateBranch}
              onSwitch={onSwitch}
              onMerge={onMerge}
              onClone={onClone}
              onPush={onPush}
              onFetch={onFetch}
              onPull={onPull}
              onAttemptConflictMerge={onAttemptConflictMerge}
              onResolveHunk={onResolveHunk}
              onMarkResolved={onMarkResolved}
              onRestore={onRestore}
              onReset={onReset}
              onRevert={onRevert}
              onReflogRecover={onReflogRecover}
              log={log}
              modifiedCount={modifiedFiles.length}
              stagedCount={stagedFiles.length}
              isRemoteLesson={isRemoteLesson}
              quizQuestions={quizQuestions}
              glossary={glossary}
              cheatsheet={cheatsheet}
              courseId={lesson.courseId}
              nextCourseId={nextCourseId}
              nextCourseTitle={nextCourseTitle}
              nextCourseBlurb={nextCourseBlurb}
              nextCourseLocked={nextCourseLocked}
              setLocation={setLocation}
            />
          </div>
        </div>

      </div>
      <Chatbot
        context={{
          screen: "lesson",
          courseId: lesson.courseId,
          courseTitle: lesson.courseTitle,
          courseDesc: COURSES.find((c) => c.id === lesson.courseId)?.desc,
          stepTitle: step.title,
          stepSub: step.sub,
          stepEyebrow: step.eyebrow,
          stepKind: step.kind,
        }}
      />
      {commitMsgOpen && (
        <CommitMessageDialog
          draft={commitMsgDraft}
          stagedFiles={stagedFiles}
          onChange={setCommitMsgDraft}
          onCancel={() => setCommitMsgOpen(false)}
          onSubmit={(msg) => {
            setCommitMsgOpen(false);
            runCommit(msg);
          }}
        />
      )}
    </div>
  );
}

function CommitMessageDialog({
  draft,
  stagedFiles,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: string;
  stagedFiles: string[];
  onChange: (v: string) => void;
  onCancel: () => void;
  onSubmit: (msg: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const trimmed = draft.trim();
  const canSubmit = trimmed.length > 0;
  function submit() {
    if (canSubmit) onSubmit(trimmed);
  }
  return (
    <div
      className="gf-ms-backdrop"
      onClick={onCancel}
      style={{ zIndex: 60 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          maxWidth: "calc(100vw - 32px)",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          padding: "24px 24px 20px",
          fontFamily: "var(--font-body)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: "var(--fg-link)",
            marginBottom: 6,
          }}
        >
          git commit
        </div>
        <h3
          style={{
            margin: "0 0 6px",
            fontSize: 18,
            color: "var(--nymbl-navy)",
          }}
        >
          Write a commit message
        </h3>
        <p
          style={{
            margin: "0 0 14px",
            fontSize: 13,
            color: "var(--fg-2)",
            lineHeight: 1.45,
          }}
        >
          Every commit needs a short message describing what changed. Future-you
          (or a teammate) will skim these later to figure out when something
          broke. Keep it in the imperative mood — "Update homepage heading",
          not "Updated…".
        </p>
        {stagedFiles.length > 0 && (
          <div
            style={{
              fontSize: 12,
              color: "var(--fg-3)",
              marginBottom: 10,
            }}
          >
            Snapshotting:{" "}
            {stagedFiles.map((f, i) => (
              <span key={f}>
                <code style={{ color: "var(--success)" }}>{f}</code>
                {i < stagedFiles.length - 1 && ", "}
              </span>
            ))}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            border: "1px solid var(--border-1)",
            borderRadius: 8,
            overflow: "hidden",
            background: "var(--bg-1)",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span
            style={{
              padding: "10px 4px 10px 12px",
              color: "var(--fg-3)",
              fontSize: 13,
              userSelect: "none",
              whiteSpace: "nowrap",
            }}
          >
            git commit -m "
          </span>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") onCancel();
            }}
            placeholder="Update homepage heading"
            style={{
              flex: 1,
              border: 0,
              outline: 0,
              padding: "10px 4px",
              fontFamily: "inherit",
              fontSize: 13,
              background: "transparent",
              color: "var(--nymbl-ink)",
              minWidth: 0,
            }}
          />
          <span
            style={{
              padding: "10px 12px 10px 4px",
              color: "var(--fg-3)",
              fontSize: 13,
              userSelect: "none",
            }}
          >
            "
          </span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 18,
          }}
        >
          <button
            className="gf-btn gf-btn--secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`gf-btn ${canSubmit ? "gf-btn--gradient" : "gf-btn--primary"}`}
            onClick={submit}
            disabled={!canSubmit}
          >
            Commit
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------- step body switch -----------------
interface StepBodyProps {
  step: LessonStep;
  repo: RepoState;
  prState: PRState;
  onPRCreate: () => void;
  onPRComment: () => void;
  onPRApprove: () => void;
  onPRMerge: () => void;
  animLastCommit: boolean;
  syncFlash: "push" | "pull" | "fetch" | null;
  activeFile: string;
  onActiveFileChange: (n: string) => void;
  onContentChange: (n: string, c: string) => void;
  onStage: () => void;
  onCommit: () => void;
  onCreateBranch: () => void;
  onSwitch: (name: string) => void;
  onMerge: () => void;
  onClone: () => void;
  onPush: () => void;
  onFetch: () => void;
  onPull: () => void;
  onAttemptConflictMerge: () => void;
  onResolveHunk: (hunkIndex: number, choice: HunkResolution, customText?: string) => void;
  onMarkResolved: () => void;
  onRestore: (filename: string) => void;
  onReset: (mode: "soft" | "mixed" | "hard", targetCommitId: string) => void;
  onRevert: (targetCommitId: string) => void;
  onReflogRecover: (reflogIndex: number) => void;
  log: LogLine[];
  modifiedCount: number;
  stagedCount: number;
  isRemoteLesson: boolean;
  quizQuestions: QuizQuestion[];
  glossary: GlossaryItem[];
  cheatsheet: CheatsheetItem[];
  courseId: string;
  nextCourseId?: string;
  nextCourseTitle?: string;
  nextCourseBlurb?: string;
  nextCourseLocked?: boolean;
  setLocation: (path: string) => void;
}

function StepBody(props: StepBodyProps) {
  const { step } = props;
  if (step.kind === "intro") return <IntroStep {...props} />;
  if (step.kind === "sandbox") {
    if (props.courseId === "undoing") return <UndoSandboxStep {...props} />;
    return <SandboxStep {...props} />;
  }
  if (step.kind === "reflog-sandbox") return <ReflogSandboxStep {...props} />;
  if (step.kind === "conflict-sandbox") return <ConflictSandboxStep {...props} />;
  if (step.kind === "conflict-anatomy") return <ConflictAnatomyStep />;
  if (step.kind === "github") return <GitHubStep repo={props.repo} />;
  if (step.kind === "pr") {
    // Has the learner pushed a feature branch to origin?
    const originFeature = props.repo.remotes?.origin?.branches?.feature;
    const featurePushed = !!originFeature;
    return (
      <PRPanel
        repo={props.repo}
        prState={props.prState}
        stepId={props.step.id}
        originalApp={ORIGINAL_APP_JSX}
        featurePushed={featurePushed}
        onCreate={props.onPRCreate}
        onComment={props.onPRComment}
        onApprove={props.onPRApprove}
        onMerge={props.onPRMerge}
      />
    );
  }
  if (step.kind === "wrap") return <WrapStep {...props} />;
  return null;
}

// ----------------- INTRO -----------------
function IntroStep({ repo, animLastCommit, syncFlash, isRemoteLesson, courseId }: StepBodyProps) {
  if (courseId === "merge-conflicts") {
    return (
      <div className="gf-step-content">
        <div>
          <div className="gf-card">
            <div className="gf-card__head">
              <h3>A conflict is a question, not a crash</h3>
            </div>
            <div className="gf-card__body gf-prose">
              <p>
                Up to now, every merge in this course has just <em>worked</em>.
                Git looked at both branches, saw the changes didn't overlap, and
                stitched them together. No drama.
              </p>
              <p>
                A <strong>merge conflict</strong> happens when both branches
                changed the <em>same lines</em> of the same file. Git won't pick
                a winner — instead it pauses the merge, opens the file, and
                writes <em>both</em> versions into it with marker lines so a
                human can decide.
              </p>
              <div className="gf-callout">
                <b>The one thing to remember</b>
                Conflicts aren't errors. The repo isn't broken. Git is asking
                you a question, and you can always answer "never mind" with{" "}
                <code>git merge --abort</code>.
              </div>
              <p>
                On the right: two people edited the same heading. You wrote one
                version on <code>main</code>, your teammate wrote a different
                one on <code>feature</code>. When you try to merge feature into
                main, git can't pick — so it writes both into App.jsx between
                conflict markers.
              </p>
              <p>
                In the next step you'll trigger that conflict yourself, look at
                what git actually writes, and then resolve it.
              </p>
            </div>
          </div>
        </div>
        <div>
          <ConflictDiagram />
        </div>
      </div>
    );
  }
  if (courseId === "undoing") {
    return (
      <div className="gf-step-content">
        <div>
          <div className="gf-card">
            <div className="gf-card__head">
              <h3>You almost never lose work in git</h3>
            </div>
            <div className="gf-card__body gf-prose">
              <p>
                The scariest thing about git, for most people, is the feeling
                that one wrong command will make a week's work vanish. Almost
                none of those stories are true. Git is <em>paranoid</em> by
                design — it keeps things for a long time, in places you don't
                see until you go looking.
              </p>
              <p>
                There are four layers where your work can live: the{" "}
                <strong>working tree</strong> (edits in your editor),{" "}
                <strong>staging</strong> (after <code>git add</code>),{" "}
                <strong>commits</strong> (after <code>git commit</code>), and
                the <strong>reflog</strong> (git's internal log of every move
                HEAD has ever made — kept for ~90 days).
              </p>
              <div className="gf-callout">
                <b>The one thing to remember</b>
                Match the undo to the layer your mistake lives in. Each layer
                has its own command, and the deeper you go, the more careful
                git is about keeping things around.
              </div>
              <p>
                On the right: the ladder of undos. Over the next four steps
                you'll use each one in a sandbox, ending with the reflog —
                where you'll "recover" commits a hard reset seemed to lose.
              </p>
            </div>
          </div>
        </div>
        <div>
          <SafetyLadder />
        </div>
      </div>
    );
  }
  if (courseId === "pull-requests") {
    return (
      <div className="gf-step-content">
        <div>
          <div className="gf-card">
            <div className="gf-card__head">
              <h3>The PR is the review surface</h3>
            </div>
            <div className="gf-card__body gf-prose">
              <p>
                In Course 02 you ran <code>git merge</code> right on your laptop —
                instant, no review, just done. That's fine for solo projects. On
                a team, almost nobody works that way.
              </p>
              <p>
                Instead: you push your feature branch to GitHub, open a{" "}
                <strong>pull request</strong>, and the merge happens <em>there</em>,
                only after someone (or several someones) has looked at the change
                and approved it.
              </p>
              <div className="gf-callout">
                <b>The one thing to remember</b>
                A pull request is a <em>proposal</em>, not the merge itself.
                Opening a PR doesn't change main — it just opens a page where
                people can review and click Merge.
              </div>
              <p>
                On the right: a <code>feature</code> branch sits off to the side
                with two new commits. The dashed arrow is the PR — it proposes
                merging those commits back into <code>main</code>. Right now,
                nothing has merged yet.
              </p>
              <p>
                Most of a PM's git life happens on this page: reading diffs,
                leaving comments, clicking Approve or Request changes. We'll
                build one from scratch in the next four steps.
              </p>
            </div>
          </div>
        </div>
        <div>
          <PRConceptDiagram />
        </div>
      </div>
    );
  }
  if (isRemoteLesson) {
    return (
      <div className="gf-step-content">
        <div>
          <div className="gf-card">
            <div className="gf-card__head">
              <h3>Two copies, one project</h3>
            </div>
            <div className="gf-card__body gf-prose">
              <p>
                Every git project you'll work on has at least{" "}
                <strong>two copies</strong>: one on your laptop, and one on
                GitHub. The copy on GitHub is called a <strong>remote</strong>,
                and by default it's named <code>origin</code>.
              </p>
              <div className="gf-callout">
                <b>The one thing to remember</b>
                Everything you do on your laptop is private until you{" "}
                <code>push</code> it. Committing doesn't share — pushing does.
              </div>
              <p>
                On the right, your laptop is empty — you haven't gotten the
                project yet. The team's work already lives on GitHub. In the
                next step, you'll <code>clone</code> it down to your machine.
              </p>
              <p>
                The two everyday commands that move work between the sides are{" "}
                <code>git push</code> (laptop → GitHub) and <code>git pull</code>{" "}
                (GitHub → laptop). That's almost the whole job.
              </p>
            </div>
          </div>
        </div>
        <div>
          <RemoteAwareGraph
            repo={repo}
            animateLast={animLastCommit}
            syncFlash={syncFlash}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="gf-step-content">
      <div>
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Mental model</h3>
          </div>
          <div className="gf-card__body gf-prose">
            <p>
              Think of your project folder as a document, and{" "}
              <strong>git</strong> as a system that takes a perfect photo every
              time you ask. Each photo is called a <strong>commit</strong>.
            </p>
            <p>
              The history of your project is just a chain of those photos, each
              one referencing the one before it. That's it. No diffs, no
              patches — just snapshots, in order.
            </p>
            <div className="gf-callout">
              <b>The one thing to remember</b>
              A commit captures every file in your project, not just the
              changes. Git stores them efficiently — but conceptually, every
              commit is the whole thing.
            </div>
            <p>
              Files in a git project live in one of three places at any given
              time:
            </p>
            <ul>
              <li>
                <b>Working tree</b> — your editor. Where you actually type. Git
                notices the file changed but isn't tracking it for the next
                commit yet.
              </li>
              <li>
                <b>Staging area</b> — a holding pen for changes you've said
                "yes, include this in the next commit." You move things here
                with <code>git add</code>.
              </li>
              <li>
                <b>History</b> — the committed snapshots. Once a change is in a
                commit, it's permanent. You move things here with{" "}
                <code>git commit</code>.
              </li>
            </ul>
            <p>
              On the right is the repository for a small React app. It has
              three commits so far, each one a snapshot of the project at a
              moment in time. Notice they all sit on the <code>main</code>{" "}
              branch — that's the default.
            </p>
            <p>
              Over the next four steps you'll walk a change through those three
              areas (edit → stage → commit), then create a branch to work on
              the side, and finally merge it back into <code>main</code>.
            </p>
          </div>
        </div>
      </div>
      <div>
        <GitGraph repo={repo} animateLast={animLastCommit} />
      </div>
    </div>
  );
}

// ----------------- STEP EXPLAINER -----------------
function StepExplainer({
  explainer,
}: {
  explainer: NonNullable<LessonStep["explainer"]>;
}) {
  return (
    <div
      className="gf-card"
      style={{ marginBottom: 18 }}
    >
      <div className="gf-card__head">
        <h3>{explainer.heading ?? "What's happening here"}</h3>
      </div>
      <div className="gf-card__body gf-prose">
        {explainer.paragraphs?.map((p, i) => (
          <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
        ))}
        {explainer.commands && explainer.commands.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              columnGap: 14,
              rowGap: 8,
              marginTop: 6,
              padding: "12px 14px",
              background: "var(--bg-accent)",
              borderRadius: 8,
              border: "1px solid var(--border-1)",
            }}
          >
            {explainer.commands.map((c, i) => (
              <div key={i} style={{ display: "contents" }}>
                <code
                  style={{
                    fontFamily: "var(--font-mono, monospace)",
                    fontSize: 12.5,
                    color: "var(--nymbl-navy)",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.cmd}
                </code>
                <div style={{ fontSize: 13, color: "var(--fg-2)" }}>
                  {c.what}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------- SANDBOX -----------------
function SandboxStep({
  step,
  repo,
  animLastCommit,
  syncFlash,
  activeFile,
  onActiveFileChange,
  onContentChange,
  onStage,
  onCommit,
  onCreateBranch,
  onSwitch,
  onMerge,
  onClone,
  onPush,
  onFetch,
  onPull,
  log,
  modifiedCount,
  stagedCount,
  isRemoteLesson,
  courseId,
}: StepBodyProps) {
  const goal = step.goal;
  const ab = isRemoteLesson ? aheadBehind(repo, repo.head || "main") : { ahead: 0, behind: 0 };
  const localEmpty = repo.commits.length === 0;

  const hasFiles = Object.keys(repo.files).length > 0;

  // Which sandbox actions are *relevant* to this step's goal. Actions that
  // aren't in this set are disabled so learners don't get sidetracked clicking
  // commands the lesson hasn't covered yet.
  const RELEVANT: Record<string, ReadonlySet<string>> = {
    stage: new Set(["stage"]),
    commit: new Set(["stage", "commit"]),
    "branch-and-commit": new Set(["stage", "commit", "branch", "switchMain"]),
    merge: new Set(["switchMain", "merge"]),
    clone: new Set(["clone"]),
    push: new Set(["branch", "stage", "commit", "push"]),
    fetch: new Set(["fetch"]),
    pull: new Set(["pull"]),
  };
  const relevant = goal && RELEVANT[goal] ? RELEVANT[goal]! : null;
  const offTopic = (id: string) => !!relevant && !relevant.has(id);

  return (
    <div className="gf-step-content gf-step-content--solo">
      {step.explainer && <StepExplainer explainer={step.explainer} />}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: hasFiles ? "minmax(0, 1fr) 1fr" : "1fr",
          gap: 18,
        }}
      >
        {hasFiles && (
          <CodeEditor
            files={repo.files}
            activeFile={activeFile}
            onActiveFileChange={onActiveFileChange}
            onContentChange={onContentChange}
          />
        )}
        {isRemoteLesson ? (
          <RemoteAwareGraph
            repo={repo}
            animateLast={animLastCommit}
            syncFlash={syncFlash}
          />
        ) : (
          <GitGraph repo={repo} animateLast={animLastCommit} />
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
          marginTop: 18,
        }}
      >
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Sandbox actions</h3>
            <div className="gf-card__head-meta">
              {modifiedCount > 0 && (
                <span style={{ color: "var(--warning)" }}>
                  {modifiedCount} modified ·{" "}
                </span>
              )}
              {stagedCount > 0 && (
                <span style={{ color: "var(--success)" }}>
                  {stagedCount} staged
                </span>
              )}
              {modifiedCount === 0 && stagedCount === 0 && (
                <span>working tree clean</span>
              )}
              {isRemoteLesson && (ab.ahead > 0 || ab.behind > 0) && (
                <span style={{ color: "var(--warning)", marginLeft: 8 }}>
                  {ab.ahead > 0 && `· ${ab.ahead} ahead`}
                  {ab.behind > 0 && ` · ${ab.behind} behind`}
                </span>
              )}
            </div>
          </div>
          <ActionRow>
            {isRemoteLesson ? (
              <>
                <Action
                  icon="Cloud"
                  label="git clone"
                  onClick={onClone}
                  disabled={offTopic("clone") || !localEmpty}
                  variant={goal === "clone" && localEmpty ? "gradient" : undefined}
                  hint="Download origin to your laptop"
                />
                {courseId === "pull-requests" && (
                  <Action
                    icon="Branch"
                    label="git switch -c feature"
                    onClick={onCreateBranch}
                    disabled={
                      offTopic("branch") ||
                      (!!repo.branches.feature && repo.head === "feature")
                    }
                    variant={
                      goal === "push" && !repo.branches.feature
                        ? "gradient"
                        : undefined
                    }
                    hint="Create a feature branch and switch to it"
                  />
                )}
                <Action
                  icon="Plus"
                  label="git add ."
                  onClick={onStage}
                  disabled={offTopic("stage") || modifiedCount === 0}
                  hint="Stage all modified files for the next commit"
                />
                <Action
                  icon="Save"
                  label="git commit"
                  onClick={onCommit}
                  disabled={offTopic("commit") || stagedCount === 0}
                  hint="Snapshot the staged changes"
                />
                <Action
                  icon="ArrowRight"
                  label="git push"
                  onClick={onPush}
                  disabled={offTopic("push") || localEmpty || ab.ahead === 0}
                  variant={goal === "push" && ab.ahead > 0 ? "gradient" : undefined}
                  hint="Send your local commits to origin"
                />
                <Action
                  icon="Refresh"
                  label="git fetch"
                  onClick={onFetch}
                  disabled={offTopic("fetch") || localEmpty}
                  variant={goal === "fetch" && ab.behind > 0 ? "gradient" : undefined}
                  hint="See what's new on origin without changing your branch"
                />
                <Action
                  icon="ChevronLeft"
                  label="git pull"
                  onClick={onPull}
                  disabled={offTopic("pull") || localEmpty || ab.behind === 0}
                  variant={goal === "pull" && ab.behind > 0 ? "gradient" : undefined}
                  hint="Bring origin's commits into your branch"
                />
              </>
            ) : (
              <>
                <Action
                  icon="Plus"
                  label="git add ."
                  onClick={onStage}
                  disabled={offTopic("stage") || modifiedCount === 0}
                  variant={
                    goal === "stage" && modifiedCount > 0 ? "gradient" : undefined
                  }
                  hint="Stage all modified files for the next commit"
                />
                <Action
                  icon="Save"
                  label="git commit"
                  onClick={onCommit}
                  disabled={offTopic("commit") || stagedCount === 0}
                  variant={
                    goal === "commit" && stagedCount > 0 ? "gradient" : undefined
                  }
                  hint="Snapshot the staged changes"
                />
                <Action
                  icon="Branch"
                  label="git switch -c feature"
                  onClick={onCreateBranch}
                  disabled={
                    offTopic("branch") ||
                    (!!repo.branches.feature && repo.head === "feature")
                  }
                  variant={
                    goal === "branch-and-commit" && !repo.branches.feature
                      ? "gradient"
                      : undefined
                  }
                  hint="Create a feature branch and switch to it"
                />
                <Action
                  icon="GitMerge"
                  label="git switch main"
                  onClick={() => onSwitch("main")}
                  disabled={offTopic("switchMain") || repo.head === "main"}
                />
                <Action
                  icon="GitMerge"
                  label="git merge feature"
                  onClick={onMerge}
                  disabled={
                    offTopic("merge") ||
                    repo.head !== "main" ||
                    !repo.branches.feature ||
                    repo.branches.main === repo.branches.feature
                  }
                  variant={
                    goal === "merge" &&
                    repo.head === "main" &&
                    !!repo.branches.feature &&
                    repo.branches.main !== repo.branches.feature
                      ? "gradient"
                      : undefined
                  }
                />
              </>
            )}
          </ActionRow>
          <div
            style={{
              padding: "14px 18px 18px",
              borderTop: "1px solid var(--border-1)",
            }}
          >
            <GoalCard
              step={step}
              repo={repo}
              modifiedCount={modifiedCount}
              stagedCount={stagedCount}
              isRemoteLesson={isRemoteLesson}
            />
          </div>
        </div>

        <TerminalLog log={log} />
      </div>
    </div>
  );
}

interface GoalCardProps {
  step: LessonStep;
  repo: RepoState;
  modifiedCount: number;
  stagedCount: number;
  isRemoteLesson: boolean;
}

function GoalCard({
  step,
  repo,
  modifiedCount,
  stagedCount,
  isRemoteLesson,
}: GoalCardProps) {
  let title = "Your goal";
  let body = "";
  let done = false;
  const ab = isRemoteLesson ? aheadBehind(repo, repo.head || "main") : { ahead: 0, behind: 0 };
  const localEmpty = repo.commits.length === 0;

  if (step.goal === "stage") {
    done = stagedCount > 0;
    if (done) {
      title = "Done — your change is staged";
      body =
        "Nice — git is now tracking your edit for the next commit. Click 'Next step →' above to continue.";
    } else {
      title = "Goal: stage your change";
      body =
        modifiedCount > 0
          ? `${modifiedCount} file modified. Click "git add ." to stage it.`
          : "Edit a file in the editor above to mark it as modified.";
    }
  } else if (step.goal === "commit") {
    title = "Goal: make your first commit";
    if (repo.commits.length > 3) {
      body = "Done! A new commit appeared on main.";
      done = true;
    } else if (stagedCount > 0) {
      body = "Click 'git commit' to snapshot your staged changes.";
    } else {
      body =
        "Stage something first (you can go back a step if needed), then commit.";
    }
  } else if (step.goal === "branch-and-commit") {
    title = "Goal: branch off, then commit on the branch";
    if (
      repo.branches.feature &&
      repo.commits.some((c) => c.branch === "feature")
    ) {
      body =
        "Done — your feature branch has its own commit, separate from main.";
      done = true;
    } else if (repo.branches.feature) {
      body = "You're on feature. Now edit a file and commit again.";
    } else {
      body = 'Click "git switch -c feature" to create the branch.';
    }
  } else if (step.goal === "merge") {
    title = "Goal: merge feature into main";
    if (
      repo.branches.feature &&
      repo.branches.main === repo.branches.feature
    ) {
      body = "Merged! main now points at the same commit as feature.";
      done = true;
    } else if (repo.head !== "main") {
      body = "Switch back to main first ('git switch main').";
    } else {
      body = "Now run 'git merge feature' to bring those commits into main.";
    }
  } else if (step.goal === "clone") {
    title = "Goal: clone origin to your laptop";
    if (!localEmpty) {
      body = "Done — your laptop now mirrors origin. Same files, same history.";
      done = true;
    } else {
      body = "Click 'git clone' to copy the project from GitHub.";
    }
  } else if (step.goal === "push") {
    title = "Goal: commit locally, then push to origin";
    if (ab.ahead === 0 && stagedCount === 0 && modifiedCount === 0 && !localEmpty) {
      body = "Pushed — your commit is now on origin. Local and origin match again.";
      done = true;
    } else if (ab.ahead > 0) {
      body = `Local is ${ab.ahead} commit${ab.ahead === 1 ? "" : "s"} ahead. Click 'git push' to send it to origin.`;
    } else if (stagedCount > 0) {
      body = "Click 'git commit' to snapshot your staged changes, then push.";
    } else if (modifiedCount > 0) {
      body = "Stage your changes, commit, then push.";
    } else {
      body = "Edit App.jsx, stage, commit — then push.";
    }
  } else if (step.goal === "fetch") {
    title = "Goal: fetch from origin";
    if (ab.behind === 0 && !localEmpty) {
      body = "Up to date — nothing new on origin to fetch.";
      done = true;
    } else {
      body = `Origin has ${ab.behind} new commit${ab.behind === 1 ? "" : "s"}. Click 'git fetch' to see it.`;
    }
  } else if (step.goal === "pull") {
    title = "Goal: pull a teammate's commit";
    if (ab.behind === 0 && !localEmpty) {
      body = "Pulled — the teammate's commit is now on your laptop.";
      done = true;
    } else if (ab.behind > 0) {
      body = `Origin is ${ab.behind} commit${ab.behind === 1 ? "" : "s"} ahead of you. Click 'git pull' to fast-forward.`;
    } else {
      body = "Waiting for origin to receive the teammate's commit…";
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: done ? "var(--success-bg)" : "var(--bg-accent)",
        border: `1px solid ${done ? "var(--success)" : "var(--fg-link)"}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: done ? "var(--success)" : "var(--fg-link)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {done ? <I.Check size={15} /> : <I.Sparkles size={14} />}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: done ? "#1F6F45" : "var(--nymbl-navy)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: "var(--fg-2)" }}>{body}</div>
      </div>
    </div>
  );
}

// ----------------- GITHUB -----------------
function GitHubStep({ repo }: { repo: RepoState }) {
  const initialApp = INITIAL_REPO.files["App.jsx"].committed;
  const currentApp = repo.files["App.jsx"].committed;
  const diffRows = computeDiff(initialApp, currentApp);

  return (
    <div className="gf-step-content">
      <div className="gf-gh">
        <div className="gf-gh__head">
          <h2 className="gf-gh__title">
            Refresh homepage copy <b>#42</b>
          </h2>
          <div className="gf-gh__meta">
            <span className="gf-gh__state">
              <I.GitMerge size={13} />
              Merged
            </span>
            <span className="gf-gh__meta-text">
              <b>erin-walsh</b> merged 2 commits into <b>main</b> from{" "}
              <b>feature</b> · 2 minutes ago
            </span>
          </div>
        </div>
        <div className="gf-gh__body">
          <div className="gf-gh__main">
            <div className="gf-gh__comment">
              <div className="gf-gh__comment-head">
                <b>erin-walsh</b> opened this on May 20
              </div>
              <div className="gf-gh__comment-body">
                Updates the homepage heading and tagline to match the latest
                brand copy. Tested locally — renders fine on all breakpoints.
              </div>
            </div>

            <div className="gf-gh__diff">
              <div className="gf-gh__diff-head">
                <span style={{ color: "#1f883d", fontWeight: 600 }}>
                  App.jsx
                </span>
                <span
                  style={{
                    color: "#59636e",
                    fontWeight: 400,
                    marginLeft: 8,
                  }}
                >
                  +{diffRows.filter((r) => r.kind === "add").length} −
                  {diffRows.filter((r) => r.kind === "del").length}
                </span>
              </div>
              {diffRows.map((row, i) => (
                <div
                  key={i}
                  className={`gf-gh__diff-row gf-gh__diff-row--${row.kind || "ctx"}`}
                >
                  <div className="gf-gh__diff-row-num">{row.lOld ?? ""}</div>
                  <div className="gf-gh__diff-row-num">{row.lNew ?? ""}</div>
                  <div className="gf-gh__diff-row-code">
                    {row.kind === "add"
                      ? "+ "
                      : row.kind === "del"
                        ? "- "
                        : "  "}
                    {row.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="gf-gh__side">
            <div className="gf-gh__section-title">Reviewers</div>
            <div className="gf-gh__avatar">
              <div
                className="gf-gh__avatar-img"
                style={{ background: "#5461AD" }}
              >
                JT
              </div>
              <div>
                <div style={{ color: "#1f2328" }}>jamie-tan</div>
                <div style={{ color: "#1a7f37", fontSize: 11 }}>
                  <I.Check size={11} style={{ verticalAlign: -1 }} /> approved
                </div>
              </div>
            </div>
            <div className="gf-gh__avatar">
              <div
                className="gf-gh__avatar-img"
                style={{ background: "#64479D" }}
              >
                SR
              </div>
              <div>
                <div style={{ color: "#1f2328" }}>sam-rao</div>
                <div style={{ color: "#59636e", fontSize: 11 }}>
                  requested
                </div>
              </div>
            </div>

            <div
              className="gf-gh__section-title"
              style={{ marginTop: 16 }}
            >
              Branch
            </div>
            <div style={{ fontSize: 12, color: "#59636e" }}>
              <div style={{ marginBottom: 4 }}>
                From: <b style={{ color: "#1f2328" }}>feature</b>
              </div>
              <div>
                Into: <b style={{ color: "#1f2328" }}>main</b>
              </div>
            </div>

            <div
              className="gf-gh__section-title"
              style={{ marginTop: 16 }}
            >
              Commits in PR
            </div>
            <div
              style={{
                fontFamily: "ui-monospace, Menlo",
                fontSize: 11,
                color: "#59636e",
                lineHeight: 1.8,
              }}
            >
              {repo.commits
                .filter(
                  (c) =>
                    c.branch === "feature" ||
                    (c.branch === "main" && c.x >= 3),
                )
                .map((c) => (
                  <div key={c.id}>
                    <span style={{ color: "#0969da" }}>
                      {c.id.slice(0, 7)}
                    </span>{" "}
                    {truncateMsg(c.msg, 24)}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="gf-card">
        <div className="gf-card__head">
          <h3>What just happened</h3>
        </div>
        <div className="gf-card__body gf-prose">
          <p>
            On a real project you wouldn't merge straight into main from your
            laptop. The team convention is:{" "}
            <strong>
              push your branch to GitHub, open a Pull Request, get review, then
              merge there
            </strong>
            . The PR is the conversation surface.
          </p>
          <p>
            The commits are the same ones you made in the simulator. The diff
            is the same diff. GitHub just dresses it up in a UI that lets your
            teammates leave comments, approve, and request changes.
          </p>
          <div className="gf-callout">
            <b>You'll be the reviewer, mostly</b>
            As a PM, you'll usually be approving or asking questions — not
            authoring code. Course 04 dives deep on what to look for during
            review.
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------- CONFLICT SANDBOX -----------------

function ConflictSandboxStep({
  step,
  repo,
  animLastCommit,
  activeFile,
  onActiveFileChange,
  onContentChange,
  onStage,
  onCommit,
  onAttemptConflictMerge,
  onResolveHunk,
  onMarkResolved,
  log,
  modifiedCount,
  stagedCount,
}: StepBodyProps) {
  const goal = step.goal;
  const inConflict = !!repo.conflict;
  const pendingMerge = !!repo.pendingMerge;
  const conflictResolvedAllHunks =
    inConflict && repo.conflict!.hunks.every((h) => h.resolution !== null);
  const fileStatus = repo.files["App.jsx"]?.status;

  return (
    <div className="gf-step-content gf-step-content--solo">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.1fr) 1fr",
          gap: 18,
        }}
      >
        <CodeEditor
          files={repo.files}
          activeFile={activeFile}
          onActiveFileChange={onActiveFileChange}
          onContentChange={onContentChange}
          readOnly={inConflict}
        />
        {goal === "resolve-conflict" && repo.conflict ? (
          <ConflictResolver
            conflict={repo.conflict}
            onResolve={onResolveHunk}
          />
        ) : (
          <GitGraph repo={repo} animateLast={animLastCommit} />
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
          marginTop: 18,
        }}
      >
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Sandbox actions</h3>
            <div className="gf-card__head-meta">
              {inConflict ? (
                <span style={{ color: "var(--danger)" }}>
                  conflicted · {repo.conflict!.hunks.filter((h) => h.resolution).length}/
                  {repo.conflict!.hunks.length} hunks resolved
                </span>
              ) : pendingMerge ? (
                <span style={{ color: "var(--warning)" }}>
                  merge staged · ready to commit
                </span>
              ) : modifiedCount > 0 ? (
                <span style={{ color: "var(--warning)" }}>
                  {modifiedCount} modified
                </span>
              ) : stagedCount > 0 ? (
                <span style={{ color: "var(--success)" }}>
                  {stagedCount} staged
                </span>
              ) : (
                <span>working tree clean</span>
              )}
            </div>
          </div>
          <ActionRow>
            <Action
              icon="GitMerge"
              label="git merge feature"
              onClick={onAttemptConflictMerge}
              disabled={inConflict || pendingMerge || goal !== "trigger-conflict"}
              variant={
                goal === "trigger-conflict" && !inConflict && !pendingMerge
                  ? "gradient"
                  : undefined
              }
              hint="Try to merge feature into main"
            />
            <Action
              icon="Check"
              label="git add App.jsx"
              onClick={onMarkResolved}
              disabled={!conflictResolvedAllHunks}
              variant={conflictResolvedAllHunks ? "gradient" : undefined}
              hint="Mark the conflict as resolved"
            />
            <Action
              icon="Save"
              label="git commit"
              onClick={onCommit}
              disabled={!pendingMerge && stagedCount === 0}
              variant={pendingMerge ? "gradient" : undefined}
              hint="Finish the merge (creates a merge commit)"
            />
            <Action
              icon="Plus"
              label="git add ."
              onClick={onStage}
              disabled={modifiedCount === 0 || inConflict}
              hint="Stage changes (non-conflict files)"
            />
          </ActionRow>
          <div
            style={{
              padding: "14px 18px 18px",
              borderTop: "1px solid var(--border-1)",
            }}
          >
            <ConflictGoalCard
              step={step}
              inConflict={inConflict}
              pendingMerge={pendingMerge}
              hunkProgress={
                repo.conflict
                  ? {
                      done: repo.conflict.hunks.filter((h) => h.resolution).length,
                      total: repo.conflict.hunks.length,
                    }
                  : null
              }
              fileStatus={fileStatus}
            />
          </div>
        </div>

        <TerminalLog log={log} />
      </div>
    </div>
  );
}

interface ConflictGoalCardProps {
  step: LessonStep;
  inConflict: boolean;
  pendingMerge: boolean;
  hunkProgress: { done: number; total: number } | null;
  fileStatus: string | undefined;
}

function ConflictGoalCard({
  step,
  inConflict,
  pendingMerge,
  hunkProgress,
  fileStatus,
}: ConflictGoalCardProps) {
  let title = "Your goal";
  let body = "";
  let done = false;

  if (step.goal === "trigger-conflict") {
    title = "Goal: trigger the conflict";
    if (inConflict) {
      title = "Goal: trigger the conflict ✓";
      body =
        "Conflict triggered. Look at App.jsx — both versions are right there, between the marker lines. Move on to step 3.";
      done = true;
    } else {
      body = "Click 'git merge feature'. Git will try, hit the same line on both branches, and stop.";
    }
  } else if (step.goal === "resolve-conflict") {
    title = "Goal: resolve the conflict and commit the merge";
    if (!inConflict && !pendingMerge && fileStatus === "clean") {
      title = "Goal: resolve the conflict and commit the merge ✓";
      body = "Merged! Look at the graph — the new commit has two parents, one from main and one from feature.";
      done = true;
    } else if (pendingMerge) {
      body = "Conflict resolved. Click 'git commit' to seal the merge.";
    } else if (hunkProgress && hunkProgress.done === hunkProgress.total) {
      body = "All hunks resolved. Click 'git add App.jsx' to mark them, then 'git commit'.";
    } else if (hunkProgress) {
      body = `Use the panel on the right to pick a resolution for each hunk (${hunkProgress.done}/${hunkProgress.total} done).`;
    } else {
      body = "Trigger the conflict first (step 2).";
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: done ? "var(--success-bg)" : "var(--bg-accent)",
        border: `1px solid ${done ? "var(--success)" : "var(--fg-link)"}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: done ? "var(--success)" : "var(--fg-link)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {done ? <I.Check size={15} /> : <I.Sparkles size={14} />}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: done ? "#1F6F45" : "var(--nymbl-navy)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: "var(--fg-2)" }}>{body}</div>
      </div>
    </div>
  );
}

// ----------------- CONFLICT ANATOMY -----------------

function ConflictAnatomyStep() {
  const markerSample = `        <h1>Welcome to Atlas</h1>
        <p>Your Business Operating System.</p>`;

  return (
    <div className="gf-step-content">
      <div>
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>The three marker lines</h3>
          </div>
          <div className="gf-card__body gf-prose">
            <p>
              Git writes exactly three special lines into a conflicted file.
              They're called <strong>conflict markers</strong>. Everything else
              you see in the file is just one of the two versions.
            </p>
            <ul>
              <li>
                <code style={{ color: "#E8A93C" }}>{"<<<<<<< HEAD (main)"}</code>{" "}
                — "below this line, until <code>=======</code>, is the version
                on the branch you're currently on" (<strong>ours</strong>).
              </li>
              <li>
                <code>{"======="}</code> — the wall between the two versions.
              </li>
              <li>
                <code style={{ color: "#2D9BD6" }}>{">>>>>>> feature"}</code> —
                "above this line, back to <code>=======</code>, is the version
                from the branch you tried to merge in" (<strong>theirs</strong>).
              </li>
            </ul>
            <div className="gf-callout">
              <b>Ours vs. theirs</b>
              "Ours" always means the branch you're on. "Theirs" always means
              the branch you asked git to merge in. On <code>main</code>{" "}
              running <code>git merge feature</code> → ours = main, theirs =
              feature.
            </div>
            <p>
              Your job is to delete <em>all</em> the marker lines and leave the
              file with the version you want. That's it — there's no special
              command, you just edit the file like any other.
            </p>
          </div>
        </div>
      </div>

      <div>
        <div
          style={{
            background: "#0B1E3F",
            border: "1px solid var(--border-1)",
            borderRadius: 14,
            padding: 18,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            height: "100%",
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: "rgba(255,255,255,0.6)",
            }}
          >
            App.jsx · annotated
          </div>
          <pre
            style={{
              margin: 0,
              fontFamily: "ui-monospace, Menlo",
              fontSize: 12,
              lineHeight: 1.7,
              color: "#fff",
              whiteSpace: "pre",
              overflow: "auto",
            }}
          >
            <span style={{ color: "rgba(255,255,255,0.5)" }}>
              {"  …\n  <main>\n"}
            </span>
            <span style={{ color: "#FFC857", fontWeight: 700 }}>
              {"<<<<<<< HEAD (main)"}
            </span>
            <span style={{ color: "rgba(255,255,255,0.5)" }}>
              {"   ← yours starts here\n"}
            </span>
            <span style={{ color: "#7BB9F0" }}>{markerSample}</span>
            <span>{"\n"}</span>
            <span style={{ color: "#A0A4B8", fontWeight: 700 }}>{"======="}</span>
            <span style={{ color: "rgba(255,255,255,0.5)" }}>
              {"                  ← separator\n"}
            </span>
            <span style={{ color: "#B084E8" }}>{markerSample}</span>
            <span>{"\n"}</span>
            <span style={{ color: "#7B4FB8", fontWeight: 700 }}>
              {">>>>>>> feature"}
            </span>
            <span style={{ color: "rgba(255,255,255,0.5)" }}>
              {"      ← theirs ended above\n  </main>\n  …"}
            </span>
          </pre>
          <div
            style={{
              fontSize: 12,
              color: "rgba(255,255,255,0.65)",
              borderTop: "1px solid rgba(255,255,255,0.1)",
              paddingTop: 10,
            }}
          >
            To resolve: delete the three marker lines and one of the two
            versions (or rewrite the lines entirely). Then <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 5px", borderRadius: 4 }}>git add</code>{" "}
            +{" "}
            <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 5px", borderRadius: 4 }}>git commit</code>.
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------- UNDO SANDBOX (course 06: restore / reset / revert) -----------------
function UndoSandboxStep({
  step,
  repo,
  animLastCommit,
  activeFile,
  onActiveFileChange,
  onContentChange,
  onRestore,
  onReset,
  onRevert,
  log,
  modifiedCount,
  stagedCount,
}: StepBodyProps) {
  const goal = step.goal;
  const oopsExists = repo.commits.some((c) => c.id === OOPS_COMMIT);
  const oopsIsTip = repo.branches[repo.head] === OOPS_COMMIT;

  return (
    <div className="gf-step-content gf-step-content--solo">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
        }}
      >
        <CodeEditor
          files={repo.files}
          activeFile={activeFile}
          onActiveFileChange={onActiveFileChange}
          onContentChange={onContentChange}
        />
        <GitGraph repo={repo} animateLast={animLastCommit} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
          marginTop: 18,
        }}
      >
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Sandbox actions</h3>
            <div className="gf-card__head-meta">
              {modifiedCount > 0 && (
                <span style={{ color: "var(--warning)" }}>
                  {modifiedCount} modified ·{" "}
                </span>
              )}
              {stagedCount > 0 && (
                <span style={{ color: "var(--success)" }}>
                  {stagedCount} staged
                </span>
              )}
              {modifiedCount === 0 && stagedCount === 0 && (
                <span>working tree clean</span>
              )}
            </div>
          </div>
          <ActionRow>
            {goal === "restore-file" && (
              <Action
                icon="Refresh"
                label="git restore App.jsx"
                onClick={() => onRestore("App.jsx")}
                disabled={modifiedCount === 0}
                variant={modifiedCount > 0 ? "gradient" : undefined}
                hint="Throw away working-tree edits on App.jsx"
              />
            )}
            {goal === "reset-commit" && oopsExists && (
              <>
                <Action
                  icon="ChevronLeft"
                  label="git reset --soft HEAD~1"
                  onClick={() => onReset("soft", GOOD_TIP_COMMIT)}
                  disabled={!oopsIsTip}
                  hint="Move main back one commit, keep the change staged"
                />
                <Action
                  icon="ChevronLeft"
                  label="git reset --mixed HEAD~1"
                  onClick={() => onReset("mixed", GOOD_TIP_COMMIT)}
                  disabled={!oopsIsTip}
                  hint="Move main back, unstage the change, keep it in the working tree"
                />
                <Action
                  icon="ChevronLeft"
                  label="git reset --hard HEAD~1"
                  onClick={() => onReset("hard", GOOD_TIP_COMMIT)}
                  disabled={!oopsIsTip}
                  variant={oopsIsTip ? "gradient" : undefined}
                  hint="Move main back AND wipe the change. Only safe on local commits."
                />
              </>
            )}
            {goal === "revert-commit" && (
              <Action
                icon="Refresh"
                label="git revert HEAD"
                onClick={() => onRevert(OOPS_COMMIT)}
                disabled={!oopsIsTip}
                variant={oopsIsTip ? "gradient" : undefined}
                hint="Create a NEW commit that undoes the bad one. Safe for shared history."
              />
            )}
          </ActionRow>
          <div
            style={{
              padding: "14px 18px 18px",
              borderTop: "1px solid var(--border-1)",
            }}
          >
            <UndoGoalCard step={step} repo={repo} modifiedCount={modifiedCount} />
          </div>
        </div>

        <TerminalLog log={log} />
      </div>
    </div>
  );
}

function UndoGoalCard({
  step,
  repo,
  modifiedCount,
}: {
  step: LessonStep;
  repo: RepoState;
  modifiedCount: number;
}) {
  let title = "Your goal";
  let body = "";
  let done = false;
  const oopsIsTip = repo.branches[repo.head] === OOPS_COMMIT;
  const onGoodTip = repo.branches[repo.head] === GOOD_TIP_COMMIT;

  if (step.goal === "restore-file") {
    title = "Goal: restore App.jsx";
    if (modifiedCount === 0 && repo.files["App.jsx"]?.status === "clean") {
      body = "Restored — App.jsx is back to its committed version.";
      done = true;
    } else {
      body = "Click 'git restore App.jsx' to throw away the typo edit.";
    }
  } else if (step.goal === "reset-commit") {
    title = "Goal: reset the bad commit off main";
    if (!oopsIsTip && onGoodTip) {
      body = "Done — main is back at the 'good' commit. The Oops commit is now orphaned (still recoverable via reflog).";
      done = true;
    } else if (oopsIsTip) {
      body = "Pick a reset flavor. --hard is the cleanest reset for this case — the bad commit was never shared.";
    } else {
      body = "main has moved off the Oops commit.";
      done = true;
    }
  } else if (step.goal === "revert-commit") {
    title = "Goal: revert the bad commit (safely)";
    if (!oopsIsTip && repo.commits.some((c) => c.msg.startsWith("Revert "))) {
      body = "Reverted — a new commit on main inverts the bad one. Both commits are still in history.";
      done = true;
    } else if (oopsIsTip) {
      body = "Click 'git revert HEAD' to add an inverse commit. Nothing is rewritten — safe even if others have pulled.";
    } else {
      body = "Run 'git revert' against the bad commit.";
    }
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        background: done ? "var(--success-bg)" : "var(--bg-accent)",
        border: `1px solid ${done ? "var(--success)" : "var(--fg-link)"}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: done ? "var(--success)" : "var(--fg-link)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {done ? <I.Check size={15} /> : <I.Sparkles size={14} />}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: done ? "#1F6F45" : "var(--nymbl-navy)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13, color: "var(--fg-2)" }}>{body}</div>
      </div>
    </div>
  );
}

// ----------------- REFLOG SANDBOX (course 06 step 5) -----------------
function ReflogSandboxStep({
  repo,
  animLastCommit,
  onReflogRecover,
  log,
}: StepBodyProps) {
  const headCommit = repo.branches[repo.head];
  const recovered =
    (repo.orphanedCommits ?? []).length === 0 &&
    headCommit === OOPS_COMMIT;

  return (
    <div className="gf-step-content gf-step-content--solo">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
        }}
      >
        <GitGraph repo={repo} animateLast={animLastCommit} />
        <Reflog
          repo={repo}
          onRecover={onReflogRecover}
          currentCommitId={headCommit}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1fr",
          gap: 18,
          marginTop: 18,
        }}
      >
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>What happened</h3>
            <div className="gf-card__head-meta">
              {(repo.orphanedCommits ?? []).length > 0
                ? `${(repo.orphanedCommits ?? []).length} commit${(repo.orphanedCommits ?? []).length === 1 ? "" : "s"} orphaned`
                : "fully recovered"}
            </div>
          </div>
          <div className="gf-card__body gf-prose">
            <p>
              When you opened this step the simulator ran{" "}
              <code>git reset --hard {REFLOG_RESET_TARGET.slice(0, 7)}</code>{" "}
              for you — which "lost" the Oops commit (you can see it dimmed on
              the graph).
            </p>
            <p>
              Look at the reflog panel on the right. Every HEAD move git has
              ever made is there, newest at the top. <code>HEAD@{`{0}`}</code>{" "}
              is the reset itself; the entry just above it is where HEAD was{" "}
              <em>before</em> the reset — i.e. the Oops commit you want back.
            </p>
            <p>
              Click <b>Recover</b> on the row for the Oops commit. The graph
              re-attaches it; the dimming clears.
            </p>
          </div>
          <div
            style={{
              padding: "14px 18px 18px",
              borderTop: "1px solid var(--border-1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                background: recovered ? "var(--success-bg)" : "var(--bg-accent)",
                border: `1px solid ${recovered ? "var(--success)" : "var(--fg-link)"}`,
                borderRadius: 10,
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  background: recovered ? "var(--success)" : "var(--fg-link)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {recovered ? <I.Check size={15} /> : <I.Sparkles size={14} />}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: recovered ? "#1F6F45" : "var(--nymbl-navy)",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: 2,
                  }}
                >
                  {recovered ? "Recovered" : "Goal: recover the orphaned commit"}
                </div>
                <div style={{ fontSize: 13, color: "var(--fg-2)" }}>
                  {recovered
                    ? "main points back at the Oops commit. Nothing was actually lost — the reflog had it the whole time."
                    : "Find the row above the most recent reset and click Recover."}
                </div>
              </div>
            </div>
          </div>
        </div>

        <TerminalLog log={log} />
      </div>
    </div>
  );
}

// ----------------- WRAP -----------------
function WrapStep({
  quizQuestions,
  glossary,
  cheatsheet,
  courseId,
  nextCourseId,
  nextCourseTitle,
  nextCourseBlurb,
  nextCourseLocked,
  setLocation,
}: StepBodyProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div className="gf-card">
        <div className="gf-card__head">
          <h3>Knowledge check</h3>
          <div className="gf-card__head-meta">{quizQuestions.length} quick questions</div>
        </div>
        <div className="gf-card__body">
          <Quiz
            questions={quizQuestions}
            onScored={(s) => setQuizScore(courseId, s)}
          />
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 18,
        }}
      >
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Glossary</h3>
            <div className="gf-card__head-meta">Terms in this lesson</div>
          </div>
          <div className="gf-card__body">
            <div className="gf-gloss">
              {glossary.map((g) => (
                <div key={g.term} className="gf-gloss__item">
                  <div className="gf-gloss__term">{g.term}</div>
                  <div className="gf-gloss__def">{g.def}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="gf-card">
          <div className="gf-card__head">
            <h3>Cheatsheet</h3>
            <div className="gf-card__head-meta">Commands you've seen</div>
          </div>
          <div className="gf-card__body">
            <div className="gf-cheat">
              {cheatsheet.map((c) => (
                <div key={c.cmd} className="gf-cheat__row">
                  <div className="gf-cheat__cmd">{c.cmd}</div>
                  <div className="gf-cheat__desc">{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {nextCourseId && nextCourseTitle && (
        <div
          style={{
            background: "var(--nymbl-navy)",
            borderRadius: 14,
            padding: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ zIndex: 1, position: "relative" }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                color: "var(--nymbl-g-cyan)",
                marginBottom: 8,
              }}
            >
              Up next
            </div>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 24,
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              {nextCourseTitle}
            </h2>
            {nextCourseBlurb && (
              <p
                style={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.65)",
                  margin: "6px 0 0",
                  maxWidth: 520,
                }}
              >
                {nextCourseBlurb}
              </p>
            )}
          </div>
          <button
            className="gf-btn gf-btn--gradient"
            style={{ position: "relative", zIndex: 1 }}
            onClick={() => setLocation(`/lesson/${nextCourseId}`)}
            disabled={nextCourseLocked}
            aria-disabled={nextCourseLocked}
            title={
              nextCourseLocked
                ? "The next course is locked by your instructor."
                : undefined
            }
          >
            {nextCourseLocked ? (
              <>
                <I.Lock size={14} /> Next course is locked
              </>
            ) : (
              <>
                Start next course <I.ArrowRight size={14} />
              </>
            )}
          </button>
          <div
            style={{
              position: "absolute",
              right: -60,
              top: -120,
              width: 320,
              height: 320,
              background: "var(--nymbl-gradient-45)",
              opacity: 0.35,
              filter: "blur(60px)",
              borderRadius: "50%",
            }}
          ></div>
        </div>
      )}
    </div>
  );
}
